import { formatFactorDataRelease } from '../../utils/factorReleasePresentation';

import { formatBetaSourceLabel } from '../../utils/betaSources';

import { optimizerContextStatusPresentation } from '../../utils/optimizerGenericGroupContract';



const formatDateTime = (value) => {
    if (!value) return 'Unknown';
    const date = parseBackendTimestamp(value);
    if (!date || Number.isNaN(date.getTime())) {
        return String(value);
    }
    return date.toLocaleString();
};

const parseBackendTimestamp = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;

    const raw = String(value).trim();
    if (!raw) return null;

    const hasTimezone = /(Z|[+-]\d{2}:\d{2})$/i.test(raw);
    const normalized = hasTimezone ? raw : `${raw}Z`;
    const parsed = new Date(normalized);
    if (!Number.isNaN(parsed.getTime())) {
        return parsed;
    }

    const fallback = new Date(raw);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
};

const formatRelativeAge = (value, nowMs = Date.now()) => {
    const date = parseBackendTimestamp(value);
    if (!date || Number.isNaN(date.getTime())) {
        return 'Unknown';
    }

    const deltaMs = nowMs - date.getTime();
    const absMinutes = Math.ceil(Math.abs(deltaMs) / 60000);
    if (deltaMs < -60000) {
        if (absMinutes < 60) return `In ${absMinutes}m`;
        const absHours = Math.ceil(absMinutes / 60);
        if (absHours < 24) return `In ${absHours}h`;
        const absDays = Math.ceil(absHours / 24);
        return `In ${absDays}d`;
    }
    if (deltaMs < 0) return 'Just now';

    const minutes = Math.floor(deltaMs / 60000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;

    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;

    const weeks = Math.floor(days / 7);
    if (weeks < 5) return `${weeks}w ago`;

    const months = Math.floor(days / 30);
    if (months < 12) return `${months}mo ago`;

    const years = Math.floor(days / 365);
    return `${years}y ago`;
};

const formatBaselineLabel = (value) => {
    if (value === 'pos_sizing') return 'Position Sizing';
    if (value === 'actual') return 'Actual';
    return 'Unknown';
};

const formatIdioSourceLabel = (value) => {
    if (value === 'ai') return 'AI';
    if (value === 'standard') return 'Standard';
    return 'Unknown';
};

const formatPersistedBetaSourceLabel = (value, availableBetaSources = null) => {
    const normalized = value == null ? '' : String(value).trim().toLowerCase();
    const isSupported = normalized === 'yahoo'
        || normalized === 'factor'
        || (normalized.startsWith('watchlist:') && normalized.length > 'watchlist:'.length);
    return isSupported ? formatBetaSourceLabel(normalized, availableBetaSources) : 'Unknown';
};

const formatFactorModelLabel = (value) => {
    const normalized = value == null ? '' : String(value).trim();
    if (!normalized) return 'Unknown';
    if (normalized.toLowerCase() === 'barra') return 'Barra';
    if (normalized.toLowerCase() === 'axioma') return 'Axioma';
    return normalized;
};

const formatFactorDataModelLabel = (value) => value || null;

const formatFactorSnapshotDateLabel = (value) => {
    const normalized = value == null ? '' : String(value).trim();
    return normalized || null;
};

const formatPosSizingContextSummary = (context = {}, availableBetaSources = null) => {
    const factorModelLabel = formatFactorModelLabel(context?.factor_model);
    const factorDataModelLabel = formatFactorDataModelLabel(context?.factor_data_model) || 'Unknown';
    const factorSnapshotDateLabel = formatFactorSnapshotDateLabel(context?.factor_snapshot_date) || 'Unknown';
    const betaSourceLabel = formatPersistedBetaSourceLabel(
        context?.beta_source,
        availableBetaSources,
    );
    const idioVolSourceLabel = formatIdioSourceLabel(context?.idio_vol_source);
    return [
        factorModelLabel,
        factorDataModelLabel,
        factorSnapshotDateLabel,
        betaSourceLabel,
        idioVolSourceLabel,
    ].join(' / ');
};

const getPortfolioContext = (item) => ({
    factor_model: item?.factorModel,
    factor_data_model: item?.factorDataModel,
    factor_snapshot_date: item?.factorSnapshotDate,
    beta_source: item?.optimizerBetaSource,
    idio_vol_source: item?.optimizerIdioVolSource,
    price_watermark: item?.savedPriceWatermark,
});

const formatOptimizerContextSummary = (item, availableBetaSources = null) => (
    formatPosSizingContextSummary(
        getPortfolioContext(item),
        availableBetaSources,
    )
);

const CONTEXT_FIELDS = [
    { key: 'factor_model', label: 'Factor provider' },
    { key: 'factor_data_model', label: 'Factor model' },
    { key: 'factor_snapshot_date', label: 'Factor data date' },
    { key: 'beta_source', label: 'Beta source' },
    { key: 'idio_vol_source', label: 'Idio volatility source' },
    { key: 'prices_date', label: 'Prices date' },
];

const getOptimizerContextPresentation = (item) => (
    item?.contextPresentation
    ?? optimizerContextStatusPresentation(item?.contextStatus, {
        receipt_status: item?.receiptStatus,
        modified_since_optimization: item?.modifiedSinceOptimization,
        is_baseline_stale: item?.isBaselineStale,
        stale_reasons: item?.staleReasons,
        compatibility: item?.compatibility,
    })
);

const formatContextFieldValue = (fieldKey, rawValue, availableBetaSources = null) => {
    if (fieldKey === 'factor_model') {
        return formatFactorModelLabel(rawValue);
    }
    if (fieldKey === 'factor_data_model') {
        return formatFactorDataModelLabel(rawValue) || 'Unknown';
    }
    if (fieldKey === 'factor_snapshot_date') {
        return formatFactorSnapshotDateLabel(rawValue) || 'Unknown';
    }
    if (fieldKey === 'beta_source') {
        return formatPersistedBetaSourceLabel(rawValue, availableBetaSources);
    }
    if (fieldKey === 'idio_vol_source') {
        return formatIdioSourceLabel(rawValue);
    }
    if (fieldKey === 'prices_date') {
        return formatDateTime(rawValue);
    }
    if (fieldKey === 'baseline_source_mode') {
        return formatBaselineLabel(rawValue);
    }
    const normalized = rawValue == null ? '' : String(rawValue).trim();
    return normalized || 'Unknown';
};

const optimizerPortfolioNeedsRefresh = (
    item,
    posSizingSyncStatus = null,
    posSizingFreshnessSummary = null,
) => {
    if (item?.type === 'opt') return getOptimizerContextPresentation(item).updateAvailable;
    if (item?.type === 'wi') {
        return Boolean(
            posSizingFreshnessSummary?.syncActionVisible
            ?? posSizingFreshnessSummary?.isStale
            ?? posSizingSyncStatus?.is_stale
        );
    }
    return false;
};

const optimizerPortfolioStatusTags = (item) => {
    if (item?.type !== 'opt') return [];
    const presentation = getOptimizerContextPresentation(item);
    const primaryStatus = (
        (!presentation.historicalAvailable && {
            key: 'unavailable',
            label: 'Needs attention',
            color: 'error',
        })
        || (presentation.restorationRequired && {
            key: 'restoration',
            label: 'Needs attention',
            color: 'error',
        })
        || (presentation.materializationState === 'modified' && {
            key: 'modified',
            label: 'Edited since optimization',
            color: 'warning',
        })
        || (presentation.updateAvailable && {
            key: 'update',
            label: 'Re-optimization available',
            color: 'warning',
        })
    );
    return primaryStatus ? [primaryStatus] : [];
};

const POS_SIZING_STALE_REASON_LABELS = {
    prices: 'prices have changed',
    context_metadata: 'context metadata is missing',
    factor_model: 'factor model changed',
    factor_data_model: 'factor data model changed',
    factor_snapshot_date: 'factor data date changed',
    factor_data_release: 'factor data was updated',
    beta_source: 'beta source changed',
    idio_vol_source: 'idio volatility source changed',
    watchlist: 'watchlist membership changed',
    state_hash: 'state hash requires sync',
    signal_scope: 'actual portfolio membership changed',
};

const formatPosSizingStaleReasonsText = (posSizingSyncStatus) => {
    const reasons = posSizingSyncStatus?.stale_reasons ?? [];
    const labels = reasons
        .map((r) => POS_SIZING_STALE_REASON_LABELS[String(r || '').trim()] || null)
        .filter(Boolean);
    return labels.length ? labels.join(', ') : 'draft is out of date';
};

const getPortfolioStaleTooltip = (
    item,
    posSizingSyncStatus = null,
    posSizingFreshnessSummary = null,
) => {
    if (item?.type === 'wi' && optimizerPortfolioNeedsRefresh(
        item,
        posSizingSyncStatus,
        posSizingFreshnessSummary,
    )) {
        return posSizingFreshnessSummary?.detailText
            || `Draft needs sync because ${formatPosSizingStaleReasonsText(posSizingSyncStatus)}.`;
    }
    if (item?.type === 'opt') {
        const presentation = getOptimizerContextPresentation(item);
        if (presentation.materializationState === 'modified') {
            return 'This portfolio was edited after it was optimized.';
        }
        if (presentation.updateAvailable) {
            return 'Some inputs have changed since this portfolio was saved.';
        }
    }
    return '';
};

const formatComparisonMembership = (value) => {
    if (!Array.isArray(value)) return 'Saved constraint membership';
    return `${value.length} securit${value.length === 1 ? 'y' : 'ies'}`;
};

const portfolioInfoKey = (item) => JSON.stringify([
    item?.id ?? null, item?.portfolioVersion ?? null, item?.receiptId ?? null,
    item?.compatibility?.current_watchlist_revision_id ?? null,
]);

const buildOptimizerChangeRows = (item, availableBetaSources = null) => {
    if (!item || item.type !== 'opt') return [];

    const presentation = getOptimizerContextPresentation(item);
    const changes = Array.isArray(presentation?.changes) ? presentation.changes : [];
    const policyFields = CONTEXT_FIELDS.filter(({ key }) => key !== 'prices_date');
    const savedContext = getPortfolioContext(item);
    const policyRows = policyFields.map(({ key, label }) => {
        const field = `risk_policy.${key}`;
        const change = changes.find((entry) => entry.field === field);
        const savedValue = formatContextFieldValue(key, change ? change.saved : savedContext[key], availableBetaSources);
        const verified = presentation.historicalAvailable
            && presentation.comparedFields?.includes('risk_policy')
            && !presentation.unknownFields?.includes('risk_policy')
            && !presentation.unknownFields?.includes(field)
            && savedValue !== 'Unknown';
        return {
            key: field, label, savedValue,
            currentValue: change ? formatContextFieldValue(key, change.current, availableBetaSources)
                : verified ? savedValue : 'Not checked',
            status: change ? 'Stale' : verified ? 'Current' : 'Unknown',
        };
    });
    const release = presentation.factorDataRelease;
    const releaseChanged = changes.some(change => ['factor_release_changed', 'factor_generation_changed'].includes(change.reason));
    const releaseKnown = presentation.comparedFields?.includes('factor_context')
        && !presentation.unknownFields?.includes('factor_context')
        && Boolean(release?.saved?.factor_as_of_date || savedContext.factor_snapshot_date);
    const releaseRow = {
        key: 'factor_data_release', label: 'Factor data release',
        savedValue: formatFactorDataRelease(release?.saved || { factor_as_of_date: savedContext.factor_snapshot_date }),
        currentValue: formatFactorDataRelease(release?.current || {
            factor_as_of_date: changes.find(change => change.field === 'factor_context.factor_as_of_date')?.current || savedContext.factor_snapshot_date,
        }),
        status: releaseChanged || release?.matches === false ? 'Stale' : releaseKnown ? 'Current' : 'Unknown',
    };
    const rows = changes.filter((change) => !change.field?.startsWith('risk_policy.')
        && !['factor_release_changed', 'factor_generation_changed'].includes(change.reason)).map((change, index) => {
        const field = String(change?.field || '');
        if (field.startsWith('enabled_group_attribute_membership.')) {
            return {
                key: `${field}:${index}`,
                label: 'Attribute constraint membership',
                savedValue: formatComparisonMembership(change.saved),
                currentValue: formatComparisonMembership(change.current),
            };
        }
        const reasonLabels = {
            factor_setting_changed: 'Risk model settings',
            msi_snapshot_changed: 'Security Resolution data',
        };
        return {
            key: `${field || change?.reason || 'change'}:${index}`,
            label: reasonLabels[change?.reason] || 'Optimizer input',
            savedValue: change.reason === 'msi_snapshot_changed' ? 'Saved security mapping' : 'Saved risk settings',
            currentValue: change.reason === 'msi_snapshot_changed' ? 'Updated security mapping' : 'Updated risk settings',
        };
    });
    return [...policyRows, releaseRow, ...rows.filter((row, index) => (
        rows.findIndex((candidate) => candidate.label === row.label
            && candidate.savedValue === row.savedValue && candidate.currentValue === row.currentValue) === index
    )).map((row) => ({ ...row, status: 'Stale' }))];
};

const formatOptimizerContextDetail = (presentation) => {
    if (presentation?.materializationState === 'modified') {
        return 'This portfolio was edited after it was optimized.';
    }
    if (presentation?.updateAvailable) {
        return 'Inputs changed since this portfolio was saved.';
    }
    if (presentation?.comparisonUnknown || presentation?.comparisonIncomplete) {
        return 'The current inputs could not be fully checked.';
    }
    return 'The saved optimization uses the current inputs.';
};



export { formatRelativeAge, formatBaselineLabel, formatOptimizerContextSummary, getOptimizerContextPresentation, optimizerPortfolioNeedsRefresh, optimizerPortfolioStatusTags, getPortfolioStaleTooltip, portfolioInfoKey, buildOptimizerChangeRows, formatOptimizerContextDetail };
